export const SITE_URL = "https://azzirevents.com";
export const SITE_NAME = "Azzir Events";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo.png`;

export const ORGANIZATION_SCHEMA = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Azzir Events",
  legalName: "Azzir Events LLP",
  url: `${SITE_URL}/`,
  logo: {
    "@type": "ImageObject",
    url: DEFAULT_OG_IMAGE,
  },
  description:
    "Discover the best events, clubs, DJ nights, artists, and nightlife near you. Explore what's happening today and this weekend across India with Azzir.",
  email: "info@azzirevents.com",
  telephone: "+91-9819955551",
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "7th Floor, Esperanca, Shahid Bhagat Singh Road, Near Electric House, Colaba",
    addressLocality: "Mumbai",
    addressRegion: "Maharashtra",
    postalCode: "400001",
    addressCountry: "IN",
  },
};

export const WEBSITE_SCHEMA = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: `${SITE_URL}/`,
  name: SITE_NAME,
  description: "Discover events, artists and clubs with Azzir Events.",
  publisher: {
    "@id": `${SITE_URL}/#organization`,
  },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [ORGANIZATION_SCHEMA, WEBSITE_SCHEMA],
};

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${SITE_URL}/about#webpage`,
  url: `${SITE_URL}/about`,
  name: "About Azzir: Clubs, Events & Artist Discovery",
  description:
    "Learn how Azzir helps people discover clubs, nightlife events and performing artists without searching across multiple platforms.",
  isPartOf: {
    "@id": `${SITE_URL}/#website`,
  },
  about: {
    "@id": `${SITE_URL}/#organization`,
  },
};

export const DEFAULT_SEO = {
  title: "Azzir: Discover Clubs, Events & Artists Near You",
  description:
    "Discover clubs, nightlife events, DJs, singers and artists performing near you. Find where to go and what’s happening with Azzir.",
  keywords:
    "Azzir Events, clubs, events, nightlife, DJs, artists, Mumbai, India",
  canonicalPath: "/",
};

export const ROUTE_SEO = {
  "/": {
    ...DEFAULT_SEO,
    schema: HOME_SCHEMA,
  },
  "/clubs": {
    title: "Clubs Near You: Nightclubs, Music & Nightlife | Azzir",
    description:
      "Discover clubs near you. Compare locations, timings, approximate cost, music genres, DJ availability and venue features on Azzir.",
    keywords: "clubs, nightlife, nightclubs, Mumbai clubs, DJ nights, Azzir",
    canonicalPath: "/clubs",
  },
  "/artists": {
    title: "Discover DJs, Singers & Performing Artists | Azzir",
    description:
      "Explore DJs, singers and performers listed in Azzir’s database. Discover their genres, profiles, associated venues and performances.",
    keywords: "artists, DJs, live music, performers, nightlife artists, Azzir",
    canonicalPath: "/artists",
  },
  "/events": {
    title: "Nightlife Events Near You: DJs, Music & Parties | Azzir",
    description:
      "Discover nightlife events near you, including DJ nights, live music, Bollywood nights, club events and artist performances.",
    keywords: "events, weekend events, festivals, parties, live music, Azzir",
    canonicalPath: "/events",
  },
  "/blog": {
    title: "Nightlife Guides, Club Picks & Event Ideas | Azzir",
    description:
      "Explore nightlife guides, club recommendations, event ideas and local entertainment stories to help you plan your next night out.",
    keywords: "Azzir blog, nightlife guides, club tips, event guides",
    canonicalPath: "/blog",
  },
  "/contact": {
    title: "Contact Azzir: Support and General Enquiries",
    description:
      "Contact Azzir for support, listing corrections, artist information, collaborations or questions about the platform.",
    keywords: "contact Azzir, event listing, partnership, nightlife support",
    canonicalPath: "/contact",
  },
  "/about": {
    title: "About Azzir: Clubs, Events & Artist Discovery",
    description:
      "Learn how Azzir helps people discover clubs, nightlife events and performing artists without searching across multiple platforms.",
    keywords: "about Azzir, event discovery app, nightlife app India",
    canonicalPath: "/about",
    schema: ABOUT_SCHEMA,
  },
  "/house-parties": {
    title: "Discover House Parties and Private Events | Azzir",
    description:
      "Discover house parties and private nightlife experiences near you. Explore available event information, locations and entertainment details.",
    keywords: "house party, private party, DJ booking, party catering Mumbai",
    canonicalPath: "/house-parties",
  },
  "/terms_conditions": {
    title: "Terms and Conditions | Azzir",
    description:
      "Read the terms and conditions governing the use of Azzir’s website, mobile application, content, listings and services.",
    keywords: "Azzir terms, terms and conditions",
    canonicalPath: "/terms_conditions",
  },
  "/privacy_policy": {
    title: "Privacy Policy | Azzir",
    description:
      "Learn how Azzir collects, uses, stores and protects personal information when users access its website, application and services.",
    keywords: "Azzir privacy policy, data protection",
    canonicalPath: "/privacy_policy",
  },
  "/signin": { noindex: true, title: "Sign In | Azzir Events" },
  "/signup": { noindex: true, title: "Sign Up | Azzir Events" },
  "/profile": { noindex: true, title: "Profile | Azzir Events" },
  "/edit-profile": { noindex: true, title: "Edit Profile | Azzir Events" },
  "/favourites": { noindex: true, title: "Favourites | Azzir Events" },
  "/followed-artists": { noindex: true, title: "Followed Artists | Azzir Events" },
  "/favourite-clubs": { noindex: true, title: "Favourite Clubs | Azzir Events" },
  "/favourite-events": { noindex: true, title: "Favourite Events | Azzir Events" },
  "/favourite-artists": { noindex: true, title: "Favourite Artists | Azzir Events" },
  "/forgot-password": { noindex: true, title: "Forgot Password | Azzir Events" },
  "/reset-password": { noindex: true, title: "Reset Password | Azzir Events" },
  "/otp": { noindex: true, title: "Verify OTP | Azzir Events" },
  "/get": { noindex: true, title: "Opening Azzir… | Azzir Events", canonicalPath: "/get" },
  "/get/": { noindex: true, title: "Opening Azzir… | Azzir Events", canonicalPath: "/get" },
};

export const CLUBS_CITY_SEO = {
  mumbai: {
    label: "Mumbai",
    names: ["Mumbai"],
    title: "Nightclubs in Mumbai: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Mumbai. Compare areas, timings, approximate cost, music genres, DJ availability and venue features on Azzir.",
    h1: "Nightclubs in Mumbai",
  },
  pune: {
    label: "Pune",
    names: ["Pune"],
    title: "Nightclubs in Pune: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Pune. Compare locations, timings, approximate cost, music genres, DJ availability and venue features.",
    h1: "Nightclubs in Pune",
  },
  goa: {
    label: "Goa",
    names: ["Goa"],
    title: "Nightclubs in Goa: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Goa. Compare locations, music genres, timings, approximate cost, DJ availability and venue features.",
    h1: "Nightclubs in Goa",
  },
  "delhi-ncr": {
    label: "Delhi NCR",
    names: ["Delhi NCR", "Delhi"],
    title: "Nightclubs in Delhi NCR: Explore Nightlife | Azzir",
    description:
      "Discover nightclubs in Delhi NCR. Compare areas, timings, approximate cost, music, DJ availability and venue features.",
    h1: "Nightclubs in Delhi NCR",
  },
  "new-delhi": {
    label: "New Delhi",
    names: ["New Delhi"],
    title: "Nightclubs in New Delhi: Explore Nightlife | Azzir",
    description:
      "Discover nightclubs in New Delhi. Compare areas, timings, approximate cost, music, DJ availability and venue features.",
    h1: "Nightclubs in New Delhi",
  },
  chandigarh: {
    label: "Chandigarh",
    names: ["Chandigarh"],
    title: "Nightclubs in Chandigarh: Explore Nightlife | Azzir",
    description:
      "Discover nightclubs in Chandigarh. Compare locations, timings, approximate cost, music, DJ availability and venue features.",
    h1: "Nightclubs in Chandigarh",
  },
  chennai: {
    label: "Chennai",
    names: ["Chennai"],
    title: "Nightclubs in Chennai: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Chennai. Compare areas, timings, approximate cost, music genres, DJ availability and venue features.",
    h1: "Nightclubs in Chennai",
  },
  bengaluru: {
    label: "Bengaluru",
    names: ["Bengaluru", "Bangalore"],
    title: "Nightclubs in Bengaluru: Explore Nightlife | Azzir",
    description:
      "Discover nightclubs in Bengaluru. Compare locations, timings, approximate cost, music, DJ availability and venue features.",
    h1: "Nightclubs in Bengaluru",
  },
  hyderabad: {
    label: "Hyderabad",
    names: ["Hyderabad"],
    title: "Nightclubs in Hyderabad: Explore Nightlife | Azzir",
    description:
      "Discover nightclubs in Hyderabad. Compare locations, timings, approximate cost, music, DJ availability and venue features.",
    h1: "Nightclubs in Hyderabad",
  },
  kolkata: {
    label: "Kolkata",
    names: ["Kolkata"],
    title: "Nightclubs in Kolkata: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Kolkata. Compare areas, timings, approximate cost, music genres, DJ availability and venue features.",
    h1: "Nightclubs in Kolkata",
  },
  dehradun: {
    label: "Dehradun",
    names: ["Dehradun"],
    title: "Nightclubs in Dehradun: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Dehradun. Compare areas, timings, approximate cost, music genres, DJ availability and venue features on Azzir.",
    h1: "Nightclubs in Dehradun",
  },
  guwahati: {
    label: "Guwahati",
    names: ["Guwahati", "Gauhati"],
    title: "Nightclubs in Guwahati: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Guwahati. Compare areas, timings, approximate cost, music genres, DJ availability and venue features on Azzir.",
    h1: "Nightclubs in Guwahati",
  },
  indore: {
    label: "Indore",
    names: ["Indore"],
    title: "Nightclubs in Indore: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Indore. Compare areas, timings, approximate cost, music genres, DJ availability and venue features on Azzir.",
    h1: "Nightclubs in Indore",
  },
  jaipur: {
    label: "Jaipur",
    names: ["Jaipur"],
    title: "Nightclubs in Jaipur: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Jaipur. Compare areas, timings, approximate cost, music genres, DJ availability and venue features on Azzir.",
    h1: "Nightclubs in Jaipur",
  },
  kochi: {
    label: "Kochi",
    names: ["Kochi", "Cochin", "Ernakulam"],
    title: "Nightclubs in Kochi: Explore Clubs & Nightlife | Azzir",
    description:
      "Discover nightclubs in Kochi. Compare areas, timings, approximate cost, music genres, DJ availability and venue features on Azzir.",
    h1: "Nightclubs in Kochi",
  },
};

const normalizeClubsCityKey = (value) =>
  `${value ?? ""}`
    .trim()
    .toLowerCase()
    .replace(/[_]+/g, "-")
    .replace(/\s+/g, "-");

export function getClubsCitySlug(cityName) {
  const key = normalizeClubsCityKey(cityName);
  if (!key) return null;

  if (CLUBS_CITY_SEO[key]) return key;

  const match = Object.entries(CLUBS_CITY_SEO).find(([, entry]) =>
    entry.names.some((name) => normalizeClubsCityKey(name) === key),
  );

  if (match) return match[0];

  // Any other city name becomes a URL slug (e.g. Thane → thane)
  return key;
}

export function getClubsCityEntry(citySlugOrName) {
  const slug = getClubsCitySlug(citySlugOrName);
  if (!slug || !CLUBS_CITY_SEO[slug]) return null;
  return { slug, ...CLUBS_CITY_SEO[slug] };
}

export function resolveClubsCitySeo(citySlugOrName) {
  const entry = getClubsCityEntry(citySlugOrName);
  if (!entry) return null;

  const canonicalPath = `/clubs/${entry.slug}`;
  return {
    title: entry.title,
    description: entry.description,
    keywords: `nightclubs ${entry.label}, clubs in ${entry.label}, nightlife ${entry.label}, Azzir`,
    h1: entry.h1,
    canonical: getCanonicalUrl(canonicalPath),
    jsonLd: null,
  };
}

export const EVENTS_CITY_SEO = {
  mumbai: {
    label: "Mumbai",
    names: ["Mumbai"],
    title: "Nightlife Events in Mumbai: DJs & Live Music | Azzir",
    description:
      "Discover nightlife events in Mumbai, including DJ nights, live music, Bollywood nights, club events and artist performances.",
    h1: "Nightlife Events in Mumbai",
  },
  pune: {
    label: "Pune",
    names: ["Pune"],
    title: "Nightlife Events in Pune: DJs & Live Music | Azzir",
    description:
      "Discover nightlife events in Pune, including DJ nights, live music, Bollywood nights, club events and artist performances.",
    h1: "Nightlife Events in Pune",
  },
  goa: {
    label: "Goa",
    names: ["Goa"],
    title: "Nightlife Events in Goa: DJs, Music & Parties | Azzir",
    description:
      "Discover nightlife events in Goa, including DJ nights, live music, club events, parties and artist performances.",
    h1: "Nightlife Events in Goa",
  },
  "delhi-ncr": {
    label: "Delhi NCR",
    names: ["Delhi NCR", "Delhi"],
    title: "Nightlife Events in Delhi NCR: DJs & Music | Azzir",
    description:
      "Discover nightlife events in Delhi NCR, including DJ nights, live music, Bollywood nights and artist performances.",
    h1: "Nightlife Events in Delhi NCR",
  },
  "new-delhi": {
    label: "New Delhi",
    names: ["New Delhi"],
    title: "Nightlife Events in New Delhi: DJs & Music | Azzir",
    description:
      "Discover nightlife events in New Delhi, including DJ nights, live music, Bollywood nights and artist performances.",
    h1: "Nightlife Events in New Delhi",
  },
  chandigarh: {
    label: "Chandigarh",
    names: ["Chandigarh"],
    title: "Nightlife Events in Chandigarh | Azzir",
    description:
      "Discover nightlife events in Chandigarh, including DJ nights, live music, Bollywood nights and artist performances.",
    h1: "Nightlife Events in Chandigarh",
  },
  chennai: {
    label: "Chennai",
    names: ["Chennai"],
    title: "Nightlife Events in Chennai: DJs & Live Music | Azzir",
    description:
      "Discover nightlife events in Chennai, including DJ nights, live music, club events and artist performances.",
    h1: "Nightlife Events in Chennai",
  },
  bengaluru: {
    label: "Bengaluru",
    names: ["Bengaluru", "Bangalore"],
    title: "Nightlife Events in Bengaluru: DJs & Music | Azzir",
    description:
      "Discover nightlife events in Bengaluru, including DJ nights, live music, club events, parties and artist performances.",
    h1: "Nightlife Events in Bengaluru",
  },
  hyderabad: {
    label: "Hyderabad",
    names: ["Hyderabad"],
    title: "Nightlife Events in Hyderabad: DJs & Music | Azzir",
    description:
      "Discover nightlife events in Hyderabad, including DJ nights, live music, Bollywood nights and artist performances.",
    h1: "Nightlife Events in Hyderabad",
  },
  kolkata: {
    label: "Kolkata",
    names: ["Kolkata"],
    title: "Nightlife Events in Kolkata: DJs & Live Music | Azzir",
    description:
      "Discover nightlife events in Kolkata, including DJ nights, live music, club events and artist performances.",
    h1: "Nightlife Events in Kolkata",
  },
  dehradun: {
    label: "Dehradun",
    names: ["Dehradun"],
    title: "Nightlife Events in Dehradun: DJs & Live Music | Azzir",
    description:
      "Discover nightlife events in Dehradun, including DJ nights, live music, club events and artist performances.",
    h1: "Nightlife Events in Dehradun",
  },
  guwahati: {
    label: "Guwahati",
    names: ["Guwahati", "Gauhati"],
    title: "Nightlife Events in Guwahati: DJs & Live Music | Azzir",
    description:
      "Discover nightlife events in Guwahati, including DJ nights, live music, club events and artist performances.",
    h1: "Nightlife Events in Guwahati",
  },
  indore: {
    label: "Indore",
    names: ["Indore"],
    title: "Nightlife Events in Indore: DJs & Live Music | Azzir",
    description:
      "Discover nightlife events in Indore, including DJ nights, live music, club events and artist performances.",
    h1: "Nightlife Events in Indore",
  },
  jaipur: {
    label: "Jaipur",
    names: ["Jaipur"],
    title: "Nightlife Events in Jaipur: DJs & Live Music | Azzir",
    description:
      "Discover nightlife events in Jaipur, including DJ nights, live music, club events and artist performances.",
    h1: "Nightlife Events in Jaipur",
  },
  kochi: {
    label: "Kochi",
    names: ["Kochi", "Cochin", "Ernakulam"],
    title: "Nightlife Events in Kochi: DJs & Live Music | Azzir",
    description:
      "Discover nightlife events in Kochi, including DJ nights, live music, club events and artist performances.",
    h1: "Nightlife Events in Kochi",
  },
};

export function getEventsCityEntry(citySlugOrName) {
  const slug = getClubsCitySlug(citySlugOrName);
  if (!slug || !EVENTS_CITY_SEO[slug]) return null;
  return { slug, ...EVENTS_CITY_SEO[slug] };
}

export function resolveEventsCitySeo(citySlugOrName) {
  const entry = getEventsCityEntry(citySlugOrName);
  if (!entry) return null;

  const canonicalPath = `/events/${entry.slug}`;
  return {
    title: entry.title,
    description: entry.description,
    keywords: `nightlife events ${entry.label}, DJ nights ${entry.label}, live music ${entry.label}, Azzir`,
    h1: entry.h1,
    canonical: getCanonicalUrl(canonicalPath),
    jsonLd: null,
  };
}

const PRIVATE_ROUTE_PREFIXES = [
  "/signin",
  "/signup",
  "/profile",
  "/edit-profile",
  "/favourites",
  "/followed-artists",
  "/favourite-clubs",
  "/favourite-events",
  "/favourite-artists",
  "/forgot-password",
  "/reset-password",
  "/otp",
  "/get",
];

const DYNAMIC_PUBLIC_PREFIXES = [
  "/club/",
  "/clubs/",
  "/artist/",
  "/artists/",
  "/event/",
  "/events/",
  "/blog/",
  "/ref/",
];

export function getCanonicalUrl(path = "/") {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized === "/") return `${SITE_URL}/`;
  return `${SITE_URL}${normalized}`;
}

export function getSeoForPath(pathname) {
  if (ROUTE_SEO[pathname]) {
    return ROUTE_SEO[pathname];
  }

  // City list SEO is applied by Clubs/Events routes via /clubs/mumbai, /events/delhi-ncr

  if (DYNAMIC_PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return null;
  }

  if (PRIVATE_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return { ...DEFAULT_SEO, noindex: true, canonicalPath: pathname };
  }

  if (pathname.startsWith("/search")) {
    return {
      title: "Search Events, Clubs & Artists | Azzir Events",
      description:
        "Search events, clubs, artists, and nightlife experiences near you with Azzir.",
      keywords: "search events, find clubs, nightlife search, Azzir",
      canonicalPath: pathname,
      noindex: true,
    };
  }

  return { ...DEFAULT_SEO, canonicalPath: pathname };
}

export function resolveRouteSeo(pathname) {
  const seo = getSeoForPath(pathname);
  if (seo === null) return null;

  const canonicalPath = seo.canonicalPath || pathname;
  return {
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    noindex: seo.noindex,
    ogType: seo.ogType,
    ogImage: seo.ogImage,
    canonical: getCanonicalUrl(canonicalPath),
    jsonLd: seo.schema || null,
  };
}

export function buildBlogPostSeo(post, slug) {
  const title = post?.seo?.title || `${post?.title || "Blog"} | Azzir Blog`;
  const description =
    post?.seo?.description || post?.excerpt || post?.title || DEFAULT_SEO.description;
  const image = post?.featuredImage || DEFAULT_OG_IMAGE;
  const canonicalPath = `/blog/${slug || post?.slug}`;

  return {
    title,
    description,
    keywords: post?.seo?.keywords || DEFAULT_SEO.keywords,
    canonical: getCanonicalUrl(canonicalPath),
    ogType: "article",
    ogImage: image,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post?.title,
      description,
      image,
      datePublished: post?.publishDate,
      author: post?.author
        ? { "@type": "Person", name: post.author }
        : { "@id": `${SITE_URL}/#organization` },
      publisher: { "@id": `${SITE_URL}/#organization` },
      mainEntityOfPage: getCanonicalUrl(canonicalPath),
    },
  };
}
